import { CognitoUserSession } from 'amazon-cognito-identity-js';

import logger from 'utils/logger';

export type AuthTransitionStatus = 'restoring' | 'transitioning' | 'stable';

export enum AuthTransitionReason {
  InitialRestore = 'initial-restore',
  OtpLogin = 'otp-login',
  JwtLogin = 'jwt-login',
  SocialLogin = 'social-login',
  Logout = 'logout',
  SessionExpired = 'session-expired',
  ForcedRefresh = 'forced-refresh',
}

export interface AuthTransitionSnapshot {
  status: AuthTransitionStatus;
  reason: AuthTransitionReason | null;
  epoch: number;
  session: CognitoUserSession | null;
}

export interface AuthTransitionRequest {
  reason: AuthTransitionReason;
  commitCredentials(): Promise<AuthCredentialCommitResult | CognitoUserSession | null>;
  bootstrapCurrentUser?(session: CognitoUserSession): Promise<unknown>;
}

export interface AuthCredentialCommitResult {
  session: CognitoUserSession | null;
  invalidatedPersistedSession?: boolean;
}

export interface AuthSessionResetContext {
  session: CognitoUserSession | null;
}

export type AuthScope = 'unresolved' | 'guest' | 'authenticated';
type ResetOutcome = 'not-required' | 'succeeded' | 'failed';
type CleanupOutcome = 'not-required' | 'succeeded' | 'failed';
type ClearAuthCache = () => Promise<unknown>;

export interface AuthUserStateCleanupContext {
  reason: AuthTransitionReason;
  previousScope: AuthScope;
  nextScope: AuthScope;
  previousEpoch: number;
  nextEpoch: number;
  invalidatedPersistedSession: boolean;
}

export interface AuthUserStateCleanupAdapter {
  name: string;
  cleanup(context: AuthUserStateCleanupContext): Promise<unknown> | unknown;
}

const UNRESOLVED_PRINCIPAL = Symbol('unresolved-principal');
const GUEST_PRINCIPAL = Symbol('guest-principal');

type PrincipalKey = typeof UNRESOLVED_PRINCIPAL | typeof GUEST_PRINCIPAL | string;

const listeners = new Set<() => void>();

let snapshot: AuthTransitionSnapshot = {
  status: 'restoring',
  reason: AuthTransitionReason.InitialRestore,
  epoch: 0,
  session: null,
};
let committedPrincipal: PrincipalKey = UNRESOLVED_PRINCIPAL;
let committedSession: CognitoUserSession | null = null;
let clearAuthCache: ClearAuthCache | null = null;
let userStateCleanupAdapters: AuthUserStateCleanupAdapter[] = [];
let authSessionResetContext: AuthSessionResetContext | null = null;
let transitionTail: Promise<void> = Promise.resolve();
let pendingTransitionCount = 0;

const publishSnapshot = (nextSnapshot: AuthTransitionSnapshot) => {
  snapshot = nextSnapshot;
  listeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      logger.error({
        message: 'Error notifying authentication transition listener.',
        errorType: errorType(error),
      });
    }
  });
};

const principalKeyForSession = (session: CognitoUserSession | null): PrincipalKey =>
  session?.getAccessToken().payload.username ?? GUEST_PRINCIPAL;

const principalScope = (principal: PrincipalKey): AuthScope => {
  if (principal === UNRESOLVED_PRINCIPAL) {
    return 'unresolved';
  }
  return principal === GUEST_PRINCIPAL ? 'guest' : 'authenticated';
};

const shouldResetForPrincipalChange = (previous: PrincipalKey, next: PrincipalKey) => {
  if (previous === UNRESOLVED_PRINCIPAL) {
    return false;
  }
  return previous !== next;
};

const durationSince = (startedAt: number) => Math.max(Date.now() - startedAt, 0);

const errorType = (error: unknown) =>
  error instanceof Error && error.name ? error.name : 'UnknownError';

export const configureAuthSessionCacheClear = (clearCache: ClearAuthCache) => {
  clearAuthCache = clearCache;
};

export const configureAuthUserStateCleanup = (adapters: AuthUserStateCleanupAdapter[]) => {
  userStateCleanupAdapters = adapters;
};

export const getAuthTransitionSnapshot = () => snapshot;

export const subscribeToAuthTransition = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getAuthenticationEpoch = () => snapshot.epoch;

export const getAuthSessionResetContext = () => authSessionResetContext;

const normalizeCredentialCommit = (
  result: AuthCredentialCommitResult | CognitoUserSession | null
): AuthCredentialCommitResult => {
  if (result && typeof result === 'object' && 'session' in result) {
    return result as AuthCredentialCommitResult;
  }
  return { session: result as CognitoUserSession | null };
};

const runUserStateCleanup = async (
  context: AuthUserStateCleanupContext
): Promise<CleanupOutcome> => {
  let cleanupOutcome: CleanupOutcome = 'succeeded';

  if (userStateCleanupAdapters.length === 0) {
    logger.error({
      message: 'Authentication user-state cleanup is not configured',
      authUserStateCleanup: context,
    });
    return 'failed';
  }

  for (const adapter of userStateCleanupAdapters) {
    const startedAt = Date.now();
    try {
      await adapter.cleanup(context);
      logger.info({
        message: 'Authentication user-state cleanup adapter completed',
        authUserStateCleanup: {
          ...context,
          adapter: adapter.name,
          outcome: 'succeeded',
          durationMs: durationSince(startedAt),
        },
      });
    } catch (error) {
      cleanupOutcome = 'failed';
      logger.error({
        message: 'Authentication user-state cleanup adapter failed',
        authUserStateCleanup: {
          ...context,
          adapter: adapter.name,
          outcome: 'failed',
          durationMs: durationSince(startedAt),
          errorType: errorType(error),
        },
      });
    }
  }

  return cleanupOutcome;
};

export const runAuthSessionTransition = ({
  reason,
  commitCredentials,
  bootstrapCurrentUser,
}: AuthTransitionRequest): Promise<CognitoUserSession | null> => {
  const startedAt = Date.now();
  pendingTransitionCount += 1;

  publishSnapshot({
    ...snapshot,
    status: reason === AuthTransitionReason.InitialRestore ? 'restoring' : 'transitioning',
    reason,
  });

  const transition = transitionTail.then(async () => {
    const previousPrincipal = committedPrincipal;
    const previousEpoch = snapshot.epoch;
    let credentialCommit: AuthCredentialCommitResult;

    try {
      credentialCommit = normalizeCredentialCommit(await commitCredentials());
    } catch (error) {
      logger.error({
        message: 'Authentication session transition failed to commit credentials',
        authTransition: {
          reason,
          previousScope: principalScope(previousPrincipal),
          nextScope: principalScope(previousPrincipal),
          previousEpoch,
          nextEpoch: previousEpoch,
          resetOutcome: 'not-required',
          cleanupOutcome: 'not-required',
          durationMs: durationSince(startedAt),
          errorType: errorType(error),
        },
      });
      throw error;
    }

    const nextSession = credentialCommit.session;
    const invalidatedPersistedSession = credentialCommit.invalidatedPersistedSession ?? false;
    const nextPrincipal = principalKeyForSession(nextSession);
    const principalChanged = shouldResetForPrincipalChange(previousPrincipal, nextPrincipal);
    const shouldCleanupUserState =
      invalidatedPersistedSession ||
      (principalChanged && principalScope(previousPrincipal) === 'authenticated');
    let nextEpoch = previousEpoch;
    let resetOutcome: ResetOutcome = 'not-required';
    let cleanupOutcome: CleanupOutcome = 'not-required';

    committedPrincipal = nextPrincipal;
    committedSession = nextSession;

    if (principalChanged) {
      nextEpoch += 1;
      publishSnapshot({ ...snapshot, epoch: nextEpoch });
    }

    if (shouldCleanupUserState) {
      cleanupOutcome = await runUserStateCleanup({
        reason,
        previousScope: principalScope(previousPrincipal),
        nextScope: principalScope(nextPrincipal),
        previousEpoch,
        nextEpoch,
        invalidatedPersistedSession,
      });
    }

    if (principalChanged) {
      try {
        if (!clearAuthCache) {
          throw new Error('Authentication session cache clearing is not configured');
        }
        authSessionResetContext = { session: nextSession };
        await clearAuthCache();
        if (nextSession && bootstrapCurrentUser) {
          await bootstrapCurrentUser(nextSession);
        }
        resetOutcome = 'succeeded';
      } catch (error) {
        resetOutcome = 'failed';
        logger.error({
          message: 'Authentication session transition completed with data reset failure',
          authTransition: {
            reason,
            previousScope: principalScope(previousPrincipal),
            nextScope: principalScope(nextPrincipal),
            previousEpoch,
            nextEpoch,
            resetOutcome,
            cleanupOutcome,
            durationMs: durationSince(startedAt),
            errorType: errorType(error),
          },
        });
      } finally {
        authSessionResetContext = null;
      }
    }

    if (resetOutcome !== 'failed') {
      logger.info({
        message: 'Authentication session transition completed',
        authTransition: {
          reason,
          previousScope: principalScope(previousPrincipal),
          nextScope: principalScope(nextPrincipal),
          previousEpoch,
          nextEpoch,
          resetOutcome,
          cleanupOutcome,
          durationMs: durationSince(startedAt),
        },
      });
    }

    return nextSession;
  });

  const settledTransition = transition.finally(() => {
    pendingTransitionCount -= 1;
    if (pendingTransitionCount === 0) {
      publishSnapshot({
        status: 'stable',
        reason: null,
        epoch: snapshot.epoch,
        session: committedSession,
      });
    }
  });

  // A rejected transition must not poison later queued transitions.
  transitionTail = settledTransition.then(
    () => undefined,
    () => undefined
  );

  return settledTransition;
};

export const resetAuthSessionTransitionForTests = () => {
  snapshot = {
    status: 'restoring',
    reason: AuthTransitionReason.InitialRestore,
    epoch: 0,
    session: null,
  };
  committedPrincipal = UNRESOLVED_PRINCIPAL;
  committedSession = null;
  clearAuthCache = null;
  userStateCleanupAdapters = [];
  authSessionResetContext = null;
  transitionTail = Promise.resolve();
  pendingTransitionCount = 0;
  listeners.clear();
};
